import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Link } from "react-router-dom";
import authAxios from "../../services/Token";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faTimes, faFilter, faFileMedical, faFlask, faBoxOpen, faEye, faEdit, faPrint } from '@fortawesome/free-solid-svg-icons';
import OrdenesDetalle from './index_detalle';

const OrdenesPrescritas = () => {
    // Dates Helper
    const getPrimerDiaMesAnterior = () => {
        const d = new Date();
        d.setMonth(d.getMonth() - 1);
        d.setDate(1);
        return d.toISOString().split('T')[0];
    };
    const getUltimoDiaMesActual = () => {
        const d = new Date();
        d.setMonth(d.getMonth() + 1);
        d.setDate(0);
        return d.toISOString().split('T')[0];
    };

    const [ordenesOriginales, setOrdenesOriginales] = useState([]);
    const [ordenesFiltradas, setOrdenesFiltradas] = useState([]);
    const [loading, setLoading] = useState(false);

    // States API Request
    const [fechaDesde, setFechaDesde] = useState(getPrimerDiaMesAnterior());
    const [fechaHasta, setFechaHasta] = useState(getUltimoDiaMesActual());

    // States local filters
    const [filtroId, setFiltroId] = useState('');
    const [filtroAutorizacion, setFiltroAutorizacion] = useState('');
    const [filtroPaciente, setFiltroPaciente] = useState('');
    const [filtroProductos, setFiltroProductos] = useState('Todas');
    const [filtroFechaSolicitud, setFiltroFechaSolicitud] = useState('');
    const [filtroNumeroSolicitud, setFiltroNumeroSolicitud] = useState('');
    const [filtroSolicitud, setFiltroSolicitud] = useState('Todas');

    // Modals state
    const [showModalOrden, setShowModalOrden] = useState(false);
    const [ordenSeleccionada, setOrdenSeleccionada] = useState(null);

    const [showModalSolicitud, setShowModalSolicitud] = useState(false);
    const [solicitudForm, setSolicitudForm] = useState({ id: '', fecha_solicitud: '', numero_solicitud: '', solicitud: 0 });

    const [showModalListados, setShowModalListados] = useState(false);
    const [listadoActivo, setListadoActivo] = useState('prescripciones'); // 'prescripciones' or 'laboratorios'
    const [listadoData, setListadoData] = useState([]);

    useEffect(() => {
        cargarOrdenes();
        // eslint-disable-next-line
    }, []);

    const cargarOrdenes = async () => {
        setLoading(true);
        try {
            const response = await authAxios.post('ordenes/mostrar_ordenes_prescritas', {
                fecha_inicial: fechaDesde,
                fecha_final: fechaHasta
            });
            if (response.data?.Status) {
                setOrdenesOriginales(response.data.ordenes);
                setOrdenesFiltradas(response.data.ordenes);
            }
        } catch (error) {
            Swal.fire('Error', 'No se pudieron cargar las órdenes prescritas', 'error');
            console.error(error);
        }
        setLoading(false);
    };

    useEffect(() => {
        aplicarFiltros();
        // eslint-disable-next-line
    }, [filtroId, filtroAutorizacion, filtroPaciente, filtroProductos, filtroFechaSolicitud, filtroNumeroSolicitud, filtroSolicitud, ordenesOriginales]);

    const aplicarFiltros = () => {
        let result = [...ordenesOriginales];

        if (filtroId) {
            result = result.filter(o => String(o.id).includes(filtroId));
        }
        if (filtroAutorizacion) {
            result = result.filter(o => String(o.orden_externa || '').includes(filtroAutorizacion));
        }
        if (filtroPaciente) {
            const searchTerms = filtroPaciente.toLowerCase();
            result = result.filter(o =>
                String(o.cedula || '').toLowerCase().includes(searchTerms) ||
                String(o.nombre || '').toLowerCase().includes(searchTerms) ||
                String(`${o.nombre} ${o.apellido} ${o.segundo_apellido}`).toLowerCase().includes(searchTerms)
            );
        }

        if (filtroProductos !== 'Todas') {
            if (filtroProductos === 'Asignados') {
                result = result.filter(o => Number(o.cant_productos) > 0);
            } else if (filtroProductos === 'No Asignados') {
                result = result.filter(o => Number(o.cant_productos) === 0);
            }
        }

        if (filtroFechaSolicitud) {
            result = result.filter(o => String(o.fecha_solicitud || '').includes(filtroFechaSolicitud));
        }
        if (filtroNumeroSolicitud) {
            result = result.filter(o => String(o.numero_solicitud || '').toLowerCase().includes(filtroNumeroSolicitud.toLowerCase()));
        }
        if (filtroSolicitud !== 'Todas') {
            if (filtroSolicitud === 'Con Solicitud') {
                result = result.filter(o => Number(o.solicitud) === 1);
            } else if (filtroSolicitud === 'Sin Solicitud') {
                result = result.filter(o => Number(o.solicitud) !== 1);
            }
        }

        setOrdenesFiltradas(result);
    };

    const limpiarFiltros = () => {
        setFiltroId('');
        setFiltroAutorizacion('');
        setFiltroPaciente('');
        setFiltroProductos('Todas');
        setFiltroFechaSolicitud('');
        setFiltroNumeroSolicitud('');
        setFiltroSolicitud('Todas');
    };

    const verOrdenModal = (orden) => {
        window.localStorage.setItem('Archivo', 200);
        window.localStorage.setItem('TablaId', orden.archivo);
        window.localStorage.setItem('OrdenId', orden.id);
        setOrdenSeleccionada(orden);
        setShowModalOrden(true);
    };

    const cerrarModalOrden = () => {
        setShowModalOrden(false);
        setOrdenSeleccionada(null);
        cargarOrdenes();
    };

    const abrirModalSolicitud = (orden) => {
        setSolicitudForm({
            id: orden.id,
            fecha_solicitud: orden.fecha_solicitud || '',
            numero_solicitud: orden.numero_solicitud || '',
            solicitud: orden.solicitud ? 1 : 0
        });
        setShowModalSolicitud(true);
    };

    const guardarSolicitud = async () => {
        try {
            const res = await authAxios.post('ordenes/actualizar_solicitud', solicitudForm);
            if (res.data.Status) {
                Swal.fire('Guardado', 'Los datos de la solicitud fueron actualizados.', 'success');
                setShowModalSolicitud(false);
                cargarOrdenes();
            }
        } catch (e) {
            Swal.fire('Error', 'No se pudo guardar la solicitud', 'error');
        }
    };

    const abrirModalListados = async (orden, tipo) => {
        setListadoActivo(tipo);
        setShowModalListados(true);
        setListadoData([]); // reset
        
        try {
            const res = await authAxios.post('ordenes/mostrar_detalles_prescripcion', { id: orden.id });
            if (res.data.Status) {
                setListadoData(tipo === 'prescripciones' ? res.data.prescripciones : res.data.laboratorios);
            }
        } catch (e) {
            console.error(e);
        }
    };

    const imprimirPDF = async (id, tabla) => {
        try {
            Swal.fire({
                title: 'Generando PDF',
                text: 'Por favor espere...',
                allowOutsideClick: false,
                didOpen: () => { Swal.showLoading(); }
            });
            const response = await authAxios.get(`/imprimir_pdf_tabla_hija/${id}/${tabla}`);
            if (response.data.status) {
                const pdfBase64 = response.data.data.pdf_base64;
                const byteCharacters = atob(pdfBase64);
                const byteNumbers = new Array(byteCharacters.length);
                for (let i = 0; i < byteCharacters.length; i++) {
                    byteNumbers[i] = byteCharacters.charCodeAt(i);
                }
                const byteArray = new Uint8Array(byteNumbers);
                const blob = new Blob([byteArray], { type: 'application/pdf' });
                const blobUrl = URL.createObjectURL(blob);
                window.open(blobUrl, '_blank');
                Swal.close();
            } else {
                Swal.fire("Error", "No se pudo generar el PDF", "error");
            }
        } catch (error) {
            Swal.fire("Error", "Error al conectar con el servidor", "error");
        }
    };

    return (
        <div className="content-wrapper">
            <div className="content">
                <div className="container-fluid pt-4">

                    {/* Tarjeta de Filtros */}
                    <div className="card shadow-sm mb-4" style={{ borderRadius: '10px' }}>
                        <div className="card-header bg-white border-bottom">
                            <h4 className="card-title text-primary m-0" style={{ fontWeight: '600' }}>
                                <FontAwesomeIcon icon={faFilter} className="me-2" />
                                Auditoría de Prescripciones y Remisiones
                            </h4>
                        </div>
                        <div className="card-body bg-light">
                            <div className="row g-3">
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">Fecha Desde</label>
                                    <input type="date" className="form-control form-control-sm" value={fechaDesde} onChange={e => setFechaDesde(e.target.value)} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">Fecha Hasta</label>
                                    <input type="date" className="form-control form-control-sm" value={fechaHasta} onChange={e => setFechaHasta(e.target.value)} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">Orden Interna</label>
                                    <input type="text" className="form-control form-control-sm" value={filtroId} onChange={e => setFiltroId(e.target.value)} placeholder="Ej: 10" />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">Orden Externa</label>
                                    <input type="text" className="form-control form-control-sm" value={filtroAutorizacion} onChange={e => setFiltroAutorizacion(e.target.value)} placeholder="Ej: 1111" />
                                </div>
                                <div className="col-md-4">
                                    <label className="form-label text-muted small fw-bold">Paciente (Céd/Nom)</label>
                                    <input type="text" className="form-control form-control-sm" value={filtroPaciente} onChange={e => setFiltroPaciente(e.target.value)} placeholder="Cédula o Nombre" />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">Productos</label>
                                    <select className="form-select form-select-sm" value={filtroProductos} onChange={e => setFiltroProductos(e.target.value)}>
                                        <option value="Todas">Todos</option>
                                        <option value="Asignados">Ya Asignados</option>
                                        <option value="No Asignados">Sin Asignar</option>
                                    </select>
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">Fecha Solicitud</label>
                                    <input type="date" className="form-control form-control-sm" value={filtroFechaSolicitud} onChange={e => setFiltroFechaSolicitud(e.target.value)} />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">N° Solicitud</label>
                                    <input type="text" className="form-control form-control-sm" value={filtroNumeroSolicitud} onChange={e => setFiltroNumeroSolicitud(e.target.value)} placeholder="Ej: 123" />
                                </div>
                                <div className="col-md-2">
                                    <label className="form-label text-muted small fw-bold">Solicitud</label>
                                    <select className="form-select form-select-sm" value={filtroSolicitud} onChange={e => setFiltroSolicitud(e.target.value)}>
                                        <option value="Todas">Todas</option>
                                        <option value="Con Solicitud">Con Solicitud</option>
                                        <option value="Sin Solicitud">Sin Solicitud</option>
                                    </select>
                                </div>
                            </div>
                            <div className="row mt-3">
                                <div className="col-12 text-end">
                                    <button className="btn btn-outline-secondary btn-sm" onClick={limpiarFiltros}>
                                        <FontAwesomeIcon icon={faTimes} className="me-1" /> Limpiar Locales
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tabla de Resultados */}
                    <div className="card shadow-sm" style={{ borderRadius: '10px' }}>
                        <div className="card-header bg-white d-flex justify-content-between align-items-center">
                            <h5 className="card-title m-0 fw-bold">Listado de Órdenes a Auditar ({ordenesFiltradas.length})</h5>
                            <button className="btn btn-primary btn-sm" onClick={cargarOrdenes} disabled={loading}>
                                {loading ? 'Cargando...' : 'Consultar en Base de Datos'}
                            </button>
                        </div>
                        <div className="card-body p-0">
                            <div className="table-responsive">
                                <table className="table table-hover table-striped align-middle m-0" style={{ fontSize: '0.9rem' }}>
                                    <thead className="table-light">
                                        <tr>
                                            <th>Orden Interna</th>
                                            <th>Orden Externa</th>
                                            <th>Fecha Ingreso</th>
                                            <th>Paciente</th>
                                            <th>Fecha Solicitud</th>
                                            <th>N° Solicitud</th>
                                            <th>Solicitud</th>
                                            <th className="text-center">Cant. Laboratorios</th>
                                            <th className="text-center">Cant. Prescripciones</th>
                                            <th className="text-center">Cant. Productos</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {ordenesFiltradas.length === 0 ? (
                                            <tr>
                                                <td colSpan="10" className="text-center py-5">
                                                    {loading ? <div>Cargando datos...</div> : <div className="text-muted"><FontAwesomeIcon icon={faSearch} size="2x" className="mb-2 d-block mx-auto" /> No hay órdenes con laboratorios ni prescripciones en este rango de fecha.</div>}
                                                </td>
                                            </tr>
                                        ) : (
                                            ordenesFiltradas.map((ord, idx) => (
                                                <tr key={idx}>
                                                    <td className="fw-bold">
                                                        <button
                                                            className="btn btn-success btn-sm fw-bold me-1"
                                                            onClick={() => verOrdenModal(ord)}
                                                            title="Ver Orden"
                                                        >
                                                            <FontAwesomeIcon icon={faEye} /> #{ord.id}
                                                        </button>
                                                    </td>
                                                    <td>
                                                        <span className="text-primary fw-bold">{ord.orden_externa || '-'}</span>
                                                    </td>
                                                    <td>{ord.fecha_inicial}</td>
                                                    <td>
                                                        <span className="d-block fw-bold">{ord.cedula}</span>
                                                        <span className="text-secondary fw-bold">
                                                            {`${ord.nombre} ${ord.segundo_nombre || ''} ${ord.apellido} ${ord.segundo_apellido || ''}`}
                                                        </span>
                                                        <div className="small text-muted mt-1">EPS ID: {ord.eps}</div>
                                                    </td>
                                                    <td>{ord.fecha_solicitud || '-'}</td>
                                                    <td>{ord.numero_solicitud || '-'}</td>
                                                    <td>
                                                        <input type="checkbox" checked={ord.solicitud == 1} readOnly className="form-check-input me-2" />
                                                        <button className="btn btn-light btn-sm" onClick={() => abrirModalSolicitud(ord)} title="Editar Solicitud">
                                                            <FontAwesomeIcon icon={faEdit} className="text-primary" />
                                                        </button>
                                                    </td>
                                                    <td className="text-center">
                                                        {Number(ord.cant_laboratorios) > 0 ? (
                                                            <button className="btn badge bg-primary fs-6 p-2 rounded-pill" onClick={() => abrirModalListados(ord, 'laboratorios')}>
                                                                <FontAwesomeIcon icon={faFlask} className="me-2" />
                                                                {ord.cant_laboratorios}
                                                            </button>
                                                        ) : (
                                                            <span className="badge bg-secondary">0</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {Number(ord.cant_prescripciones) > 0 ? (
                                                            <button className="btn badge bg-info text-dark fs-6 p-2 rounded-pill" onClick={() => abrirModalListados(ord, 'prescripciones')}>
                                                                <FontAwesomeIcon icon={faFileMedical} className="me-2" />
                                                                {ord.cant_prescripciones}
                                                            </button>
                                                        ) : (
                                                            <span className="badge bg-secondary">0</span>
                                                        )}
                                                    </td>
                                                    <td className="text-center">
                                                        {Number(ord.cant_productos) > 0 ? (
                                                            <span className="badge bg-success fs-6 p-2 rounded-pill">
                                                                <FontAwesomeIcon icon={faBoxOpen} className="me-2" />
                                                                {ord.cant_productos}
                                                            </span>
                                                        ) : (
                                                            <span className="badge bg-secondary">0</span>
                                                        )}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>

                </div>
            </div>

            {/* MODAL VER ORDEN */}
            {showModalOrden && ordenSeleccionada && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex="-1">
                    <div className="modal-dialog modal-xl modal-dialog-scrollable">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Orden #{ordenSeleccionada.id} — {ordenSeleccionada.nombre} {ordenSeleccionada.apellido}</h5>
                                <button type="button" className="btn-close" onClick={cerrarModalOrden}></button>
                            </div>
                            <div className="modal-body"><OrdenesDetalle /></div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={cerrarModalOrden}>
                                    <FontAwesomeIcon icon={faTimes} /> Cerrar
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL EDITAR SOLICITUD */}
            {showModalSolicitud && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex="-1">
                    <div className="modal-dialog">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">Editar Solicitud - Orden #{solicitudForm.id}</h5>
                                <button type="button" className="btn-close" onClick={() => setShowModalSolicitud(false)}></button>
                            </div>
                            <div className="modal-body">
                                <div className="mb-3">
                                    <label className="form-label fw-bold">Fecha Solicitud</label>
                                    <input type="date" className="form-control" value={solicitudForm.fecha_solicitud} onChange={e => setSolicitudForm({...solicitudForm, fecha_solicitud: e.target.value})} />
                                </div>
                                <div className="mb-3">
                                    <label className="form-label fw-bold">N° Solicitud</label>
                                    <input type="text" className="form-control" value={solicitudForm.numero_solicitud} onChange={e => setSolicitudForm({...solicitudForm, numero_solicitud: e.target.value})} />
                                </div>
                                <div className="form-check">
                                    <input type="checkbox" className="form-check-input" id="checkSolicitud" checked={solicitudForm.solicitud === 1} onChange={e => setSolicitudForm({...solicitudForm, solicitud: e.target.checked ? 1 : 0})} />
                                    <label className="form-check-label fw-bold" htmlFor="checkSolicitud">Solicitud Activa</label>
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModalSolicitud(false)}>Cancelar</button>
                                <button type="button" className="btn btn-primary" onClick={guardarSolicitud}>Guardar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* MODAL LISTADOS (PRESCRIPCIONES/LABORATORIOS) */}
            {showModalListados && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 1060 }} tabIndex="-1">
                    <div className="modal-dialog modal-lg">
                        <div className="modal-content">
                            <div className="modal-header">
                                <h5 className="modal-title">
                                    {listadoActivo === 'prescripciones' ? 'Prescripciones de la Orden' : 'Laboratorios (Remisiones) de la Orden'}
                                </h5>
                                <button type="button" className="btn-close" onClick={() => setShowModalListados(false)}></button>
                            </div>
                            <div className="modal-body">
                                {listadoData.length === 0 ? (
                                    <div className="text-center text-muted py-4">Cargando o sin registros...</div>
                                ) : (
                                    <table className="table table-sm table-bordered table-striped">
                                        <thead className="table-light">
                                            <tr>
                                                <th>ID</th>
                                                <th>Fecha</th>
                                                {listadoActivo === 'laboratorios' && <th>Concepto</th>}
                                                <th className="text-center">Imprimir</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {listadoData.map(item => (
                                                <tr key={item.id}>
                                                    <td>{item.id}</td>
                                                    <td>{item.fecha}</td>
                                                    {listadoActivo === 'laboratorios' && <td>{item.concepto}</td>}
                                                    <td className="text-center">
                                                        <button 
                                                            className="btn btn-danger btn-sm" 
                                                            title="Imprimir PDF"
                                                            onClick={() => imprimirPDF(item.id, listadoActivo === 'prescripciones' ? 'prescripcion' : 'laboratorios')}
                                                        >
                                                            <FontAwesomeIcon icon={faPrint} />
                                                        </button>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                )}
                            </div>
                            <div className="modal-footer">
                                <button type="button" className="btn btn-secondary" onClick={() => setShowModalListados(false)}>Cerrar</button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

        </div>
    );
};

export default OrdenesPrescritas;
